import { z } from 'zod';
import { TASK_EXECUTION_TYPE, TASK_PRIORITY, TASK_STATUS } from '../constnats/task';

const taskIdSchema = z.number().int().positive();
export type TaskIdType = z.infer<typeof taskIdSchema>;

const assignedUserIdSchema = z.number().int().positive().nullable();

const createdByUserId = z.number().int().positive();

const statusUpdatedByUserIdSchema = z.number().int().positive().nullable();

const taskDescriptionSchema = z
    .string()
    .max(2000, 'Task Description cannot be more than 2000 characters')
    .min(1, 'Task Description cannot be empty!');

const taskPrioritySchema = z.nativeEnum(TASK_PRIORITY);

const dateSchema = z.string();

const taskStatusSchema = z.nativeEnum(TASK_STATUS);

export type TaskStatusType = z.infer<typeof taskStatusSchema>;

const taskTitleSchema = z
    .string()
    .max(255, 'Task Title cannot be more than 255 characters')
    .min(1, 'Task Title cannot be empty!');

const taskExecutionNotesSchema = z.string().max(1000, 'Execution Notes cannot be more than 1000 characters');

const taskExecutionIdSchema = z.number().int().positive();
export type TaskExecutionIdType = z.infer<typeof taskExecutionIdSchema>;

const entityIdSchema = z.number().int().positive();
const entityTypeSchema = z.nativeEnum(TASK_EXECUTION_TYPE);

export const taskSchema = z.object({
    assignedUserId: assignedUserIdSchema,
    createdByUserId: createdByUserId,
    createdOn: dateSchema,
    description: taskDescriptionSchema,
    dueDate: dateSchema,
    id: taskIdSchema,
    priority: taskPrioritySchema,
    status: taskStatusSchema,
    statusUpdatedByUserId: statusUpdatedByUserIdSchema,
    title: taskTitleSchema,
    updatedOn: dateSchema,
});

export type TaskType = z.infer<typeof taskSchema>;

export const taskInfoSchema = taskSchema.pick({
    assignedUserId: true,
    dueDate: true,
    id: true,
    priority: true,
    status: true,
    title: true,
});

export type TaskInfoType = z.infer<typeof taskInfoSchema>;

export const taskCreationSchema = taskSchema
    .pick({
        assignedUserId: true,
        description: true,
        dueDate: true,
        priority: true,
        title: true,
    })
    .extend({ createdById: z.number() });

export type TaskCreationSchemaType = z.infer<typeof taskCreationSchema>;

export const taskUpdateSchema = taskSchema
    .pick({
        assignedUserId: true,
        description: true,
        dueDate: true,
        priority: true,
        status: true,
        title: true,
    })
    .extend({
        taskId: taskIdSchema,
    });

export type TaskUpdateSchemaType = z.infer<typeof taskUpdateSchema>;

export const executionConfigSchema = z.object({
    entityId: entityIdSchema,
    entityType: entityTypeSchema,
});

export type ExecutionConfigType = z.infer<typeof executionConfigSchema>;

export const taskExecutionInfoSchema = executionConfigSchema.extend({
    entityName: z.string(),
    id: taskExecutionIdSchema,
    notes: taskExecutionNotesSchema,
    status: taskStatusSchema,
    statusUpdatedByUserId: statusUpdatedByUserIdSchema,
    taskId: taskIdSchema,
});

export type TaskExecutionInfoType = z.infer<typeof taskExecutionInfoSchema>;

export const taskExecutionsSetSchema = z.object({
    taskId: taskIdSchema,
    executionConfigs: z.array(executionConfigSchema),
});

export type TaskExecutionsSetSchemaType = z.infer<typeof taskExecutionsSetSchema>;

export const taskExecutionsCreationSchema = z.object({
    taskId:taskIdSchema,
    entities: z.array(executionConfigSchema)
}) 

export type TaskExecutionsCreationSchemaType = z.infer<typeof taskExecutionsCreationSchema>;

export const taskExecutionUpdateSchema = z.object({
    executionId: taskExecutionIdSchema,
    notes: taskExecutionNotesSchema,
    status: taskStatusSchema,
    taskId: taskIdSchema,
});

export type TaskExecutionUpdateSchemaType = z.infer<typeof taskExecutionUpdateSchema>;

export const taskExecutionDetailsSchema = executionConfigSchema.extend({
    dueDate: dateSchema,
    entityName: z.string(),
    executionId: taskExecutionIdSchema,
    notes: taskExecutionNotesSchema,
    status: taskStatusSchema,
    statusUpdatedById: statusUpdatedByUserIdSchema,
    taskDescription: taskDescriptionSchema,
    taskId: taskIdSchema,
    taskPriority: taskPrioritySchema,
    taskTitle: taskTitleSchema,
    createdByUserId: createdByUserId,
}).transform(val => {
    const {executionId, ...rest} = val
    return {
        ...rest,
        id: executionId
    }
});

export type TaskExecutionDetailsType = z.infer<typeof taskExecutionDetailsSchema>;

export const listTasksFiltersSchema = z.object({
    statuses: z.array(taskStatusSchema).optional(),
    assignedUserId: assignedUserIdSchema.optional(),
    page: z.number(),
});

export type ListTasksFiltersType = z.infer<typeof listTasksFiltersSchema>;

export type ListTasksFilterWithoutPage = Omit<ListTasksFiltersType, 'page'>;
